import { useQuery } from "@tanstack/react-query";
import {
  BarChart3, Briefcase, CalendarDays, ChevronRight, Flag, Globe2,
  HardHat, LayoutDashboard, ListChecks, LogOut, Menu,
  MessageSquare, Phone, Plug, Settings, ShieldCheck, Tag,
  UserRound, Users, X,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useNavigate } from "react-router-dom";

import { logout, me, type CurrentUser } from "../../api/auth.ts";
import { cn } from "../../lib/cn.ts";
import GlobalSearch from "./GlobalSearch.tsx";

const NAV_GROUPS = [
  {
    label: "Operations",
    items: [
      { to: "/dashboard", label: "nav.dashboard", icon: LayoutDashboard, roles: ["admin", "dispatcher", "technician"] },
      { to: "/leads",     label: "nav.leads",     icon: ListChecks,      roles: ["admin", "dispatcher", "technician"] },
      { to: "/clients",   label: "nav.clients",   icon: UserRound,       roles: ["admin", "dispatcher"] },
      { to: "/jobs",      label: "nav.jobs",       icon: Briefcase,       roles: ["admin", "dispatcher", "technician"] },
      { to: "/calendar",  label: "nav.calendar",  icon: CalendarDays,    roles: ["admin", "dispatcher", "technician"] },
      { to: "/technicians", label: "nav.technicians", icon: HardHat,     roles: ["admin", "dispatcher"] },
    ],
  },
  {
    label: "Communications",
    items: [
      { to: "/calls", label: "nav.calls", icon: Phone,         roles: ["admin", "dispatcher"] },
      { to: "/sms",   label: "nav.sms",   icon: MessageSquare, roles: ["admin", "dispatcher"] },
    ],
  },
  {
    label: "Settings",
    items: [
      { to: "/service-catalog", label: "nav.serviceCatalog", icon: Tag,         roles: ["admin"] },
      { to: "/sites",           label: "nav.sites",           icon: Globe2,      roles: ["admin", "dispatcher"] },
      { to: "/users",           label: "nav.users",           icon: Users,       roles: ["admin", "dispatcher"] },
      { to: "/integrations",    label: "nav.integrations",    icon: Plug,        roles: ["admin", "dispatcher"] },
      { to: "/google-ads",      label: "nav.googleAds",       icon: BarChart3,   roles: ["admin", "dispatcher"] },
      { to: "/feature-flags",   label: "nav.featureFlags",    icon: Flag,        roles: ["admin", "dispatcher"] },
      { to: "/audit",           label: "nav.audit",           icon: ShieldCheck, roles: ["admin"] },
      { to: "/settings",        label: "nav.settings",        icon: Settings,    roles: ["admin", "dispatcher", "technician"] },
    ],
  },
];

export default function AppLayout() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { data: user } = useQuery<CurrentUser>({ queryKey: ["auth", "me"], queryFn: me });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const handleLogout = async () => { await logout(); nav("/login", { replace: true }); };
  const closeMobileNav = () => setMobileNavOpen(false);

  const visibleGroups = NAV_GROUPS.map(g => ({
    ...g,
    items: g.items.filter(n => !user || n.roles.includes(user.role)),
  })).filter(g => g.items.length > 0);

  return (
    <div className="grid h-screen grid-rows-[3.5rem_1fr] md:grid-cols-[15rem_1fr]">

      {/* ── Header ── */}
      <header className="z-20 flex items-center justify-between border-b border-slate-200 bg-white px-4 shadow-sm md:col-span-2 md:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 md:hidden"
            onClick={() => setMobileNavOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-600">
              <BarChart3 className="h-4 w-4 text-white" />
            </div>
            <span className="truncate text-sm font-bold tracking-tight text-slate-900">{t("app.name")}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <GlobalSearch />
          {user && (
            <div className="hidden items-center gap-2 sm:flex">
              <span className="text-sm font-medium text-slate-700">{user.full_name}</span>
              <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {user.role}
              </span>
            </div>
          )}
          <button type="button" className="btn-secondary !px-2.5 sm:!px-4" onClick={handleLogout}>
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">{t("nav.logout")}</span>
          </button>
        </div>
      </header>

      {/* Mobile overlay */}
      {mobileNavOpen && (
        <button
          aria-label="Close menu"
          className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm md:hidden"
          onClick={closeMobileNav}
        />
      )}

      {/* ── Sidebar ── */}
      <aside
        className={cn(
          "fixed inset-y-0 start-0 top-14 z-40 flex w-60 flex-col border-e border-slate-200 bg-white transition-transform duration-200",
          "md:static md:top-0 md:row-start-2 md:translate-x-0",
          mobileNavOpen ? "translate-x-0" : "-translate-x-full rtl:translate-x-full md:translate-x-0",
        )}
      >
        <div className="flex items-center justify-end px-3 pt-2 md:hidden">
          <button className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" onClick={closeMobileNav}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-5">
          {visibleGroups.map((group) => (
            <div key={group.label}>
              <p className="mb-1.5 px-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                {group.label}
              </p>
              <div className="space-y-0.5">
                {group.items.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    onClick={closeMobileNav}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150",
                        isActive
                          ? "bg-brand-50 text-brand-700"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <n.icon className={cn("h-4 w-4 shrink-0", isActive ? "text-brand-600" : "text-slate-400")} />
                        <span className="flex-1">{t(n.label)}</span>
                        {isActive && <ChevronRight className="h-3 w-3 text-brand-400" />}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* User footer */}
        {user && (
          <div className="border-t border-slate-200 px-4 py-3">
            <p className="text-xs font-semibold text-slate-700 truncate">{user.full_name}</p>
            <p className="text-xs text-slate-400 capitalize">{user.email}</p>
          </div>
        )}
      </aside>

      <main className="min-w-0 overflow-y-auto bg-slate-50/70 p-4 md:col-start-2 md:row-start-2 md:p-6">
        <Outlet />
      </main>
    </div>
  );
}
